/**
 * Isolated printing layer with a triple engine:
 * - Silent desktop printing: when the site runs inside the RKC desktop shell
 *   (Electron) and the preload bridge exposes `window.electronAPI.printSilent`,
 *   the ticket is sent straight to the configured printer with NO OS dialog.
 * - Legacy desktop bridge: when only the classic `print(request)` contract exists,
 *   it is used (the shell receives the full HTML document).
 * - Plain browser: prints ONLY the invoice document from an off-screen iframe
 *   (never the visible app window), keeping the receipt sharp and unclipped.
 *
 * The web UI never needs to change: the shell only exposes the bridge on `window`.
 */

export const POS_WIDTHS = [58, 80] as const;

export type PrintJobKind = "ticket" | "a4";

export interface PrintJobOptions {
  /** Target printer name (as registered in the operating system). */
  printer?: string | null;
  /** Paper width for thermal tickets (58 / 80 mm). */
  widthMm?: number;
  /** Number of copies to print. */
  copies?: number;
  /** Ask the bridge to open the cash drawer after printing. */
  openDrawer?: boolean;
  /** Document kind: thermal ticket or A4 page. */
  kind?: PrintJobKind;
}

export interface RkcDesktopPrintRequest extends PrintJobOptions {
  html: string;
  kind: PrintJobKind;
}

export interface RkcDesktopPrintResult {
  ok?: boolean;
  success?: boolean;
  message?: string;
  error?: string;
}

/** Result shape returned by the silent bridge (`electronAPI.printSilent`). */
export interface RkcDesktopSilentResult {
  success?: boolean;
  message?: string;
  error?: string;
}

/**
 * Payload sent to the silent bridge. It carries the ticket/invoice HTML so the
 * shell can render it in a HIDDEN window and print ONLY that content, instead
 * of printing the visible app window (which produced a "screenshot" ticket).
 */
export interface RkcSilentPrintPayload {
  /** Target printer name resolved to the OS spelling, or null for the default. */
  printer?: string | null;
  /** Full HTML document to print. */
  html: string;
  /** Document kind: thermal ticket or A4 page. */
  kind: PrintJobKind;
  /** Paper width for thermal tickets (58 / 80 mm). */
  widthMm?: number;
  /** Number of copies to print. */
  copies?: number;
  /** Ask the shell to open the cash drawer after printing. */
  openDrawer?: boolean;
}

/**
 * Contract that the Electron shell may expose on `window`.
 * The web app only *detects* this object; it never creates it.
 */
export interface RkcDesktopBridge {
  version?: string;
  /**
   * Silent print. Receives the full payload (printer + HTML document) so the
   * shell can print only the ticket from a hidden window. A bare string is
   * still accepted for backwards compatibility with older shells.
   */
  printSilent?: (
    payload?: RkcSilentPrintPayload | string | null,
  ) => Promise<RkcDesktopSilentResult | void> | RkcDesktopSilentResult | void;
  /** Legacy bridge: receives the full print request including the HTML document. */
  print?: (request: RkcDesktopPrintRequest) => Promise<RkcDesktopPrintResult | void> | RkcDesktopPrintResult | void;
  openDrawer?: (printer?: string | null) => void | Promise<void>;
  listPrinters?: () => Promise<string[]> | string[];
}

declare global {
  interface Window {
    rkcDesktop?: RkcDesktopBridge;
    electronAPI?: RkcDesktopBridge;
  }
}

/**
 * Fallback thermal printer name, used only when the app is running inside the
 * desktop shell but no printer has been configured in Ajustes > Impresoras.
 */
export const DEFAULT_THERMAL_PRINTER = "2C-POS80-01-V6 Printer(2)";

/** Returns the injected desktop bridge, if the site is running inside the Electron shell. */
export function getDesktopBridge(): RkcDesktopBridge | null {
  if (typeof window === "undefined") return null;
  const win = window as Window;
  const candidates = [win.electronAPI, win.rkcDesktop].filter(Boolean) as RkcDesktopBridge[];
  // Prefer the silent bridge when it is exposed on either object.
  const silent = candidates.find((candidate) => typeof candidate.printSilent === "function");
  if (silent) return silent;
  for (const candidate of candidates) {
    if (typeof candidate.print === "function" || typeof candidate.openDrawer === "function") {
      return candidate;
    }
  }
  return null;
}

/** True when the app is running inside the desktop shell (bridge present or Electron user agent). */
export function isDesktopApp(): boolean {
  if (getDesktopBridge()) return true;
  if (typeof navigator !== "undefined" && /electron/i.test(navigator.userAgent)) return true;
  return false;
}

/** Picks the printer name to send to the silent bridge. */
function normalizeName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Returns the OS printer name that matches `desired` (case/space-insensitive), or null. */
function matchPrinter(available: string[], desired: string): string | null {
  if (!desired) return null;
  const target = normalizeName(desired);
  const exact = available.find((name) => normalizeName(name) === target);
  if (exact) return exact;
  const partial = available.find((name) => normalizeName(name).includes(target));
  return partial ?? null;
}

/**
 * Asks the desktop shell for the list of printers installed in the OS.
 * Returns an empty array when the shell does not expose `listPrinters`.
 */
export async function listAvailablePrinters(
  bridge: RkcDesktopBridge | null = getDesktopBridge(),
): Promise<string[]> {
  if (!bridge?.listPrinters) return [];
  try {
    const list = await Promise.resolve(bridge.listPrinters());
    if (!Array.isArray(list)) return [];
    return list.filter((name): name is string => typeof name === "string" && name.trim() !== "");
  } catch {
    return [];
  }
}

/**
 * Resolves the exact printer name to send to the silent bridge.
 * When the shell exposes the installed printers, we validate against the real
 * names (Windows is strict about `deviceName`), returning the OS spelling.
 * If the configured printer is not installed, we return null so the shell can
 * fall back to its default printer instead of failing with "Invalid deviceName".
 */
async function resolveSilentPrinter(
  bridge: RkcDesktopBridge,
  options: PrintJobOptions,
): Promise<string | null> {
  const configured = options.printer?.trim() || "";
  const fallback = (options.kind ?? "ticket") === "ticket" ? DEFAULT_THERMAL_PRINTER : "";
  const desired = configured || fallback;

  const available = await listAvailablePrinters(bridge);
  if (available.length > 0) {
    const match = matchPrinter(available, desired);
    if (match) return match;
    // Configured/fallback printer is not installed: let the shell use its default.
    return null;
  }

  return desired || null;
}

/**
 * Sends the print order to the Electron silent bridge and reports the outcome.
 * The full document HTML travels with the request so the shell prints ONLY the
 * ticket/content from a hidden window (never the visible app window).
 * Returns true when the job was handled by the desktop shell.
 */
function printViaSilentBridge(bridge: RkcDesktopBridge, html: string, options: PrintJobOptions): boolean {
  void (async () => {
    try {
      const printer = await resolveSilentPrinter(bridge, options);
      const payload: RkcSilentPrintPayload = {
        printer,
        html,
        kind: options.kind ?? "ticket",
        widthMm: options.widthMm,
        copies: options.copies ?? 1,
        openDrawer: options.openDrawer ?? false,
      };
      const result = await Promise.resolve(bridge.printSilent?.(payload));
      const outcome = result as RkcDesktopSilentResult | undefined;
      if (outcome && outcome.success === false) {
        console.error("Error en la impresora:", outcome.error ?? outcome.message ?? "Desconocido");
        return;
      }
      console.log("Impresión directa exitosa:", outcome?.message ?? "OK");
    } catch (error) {
      console.error("Error de comunicación con Electron:", error);
    }
  })();
  return true;
}

/** Id of the isolated, off-screen iframe used to print only the document. */
const PRINT_FRAME_ID = "rkc-print-frame";

/** True when the string is already a complete HTML document. */
function isFullDocument(html: string): boolean {
  const trimmed = html.trimStart().toLowerCase();
  return trimmed.startsWith("<!doctype") || trimmed.startsWith("<html");
}

/**
 * Wraps a bare HTML fragment (e.g. the printer test ticket) into a standalone,
 * print-ready document so it prints crisp and correctly sized on its own,
 * instead of relying on the styles of the visible application window.
 */
function ensurePrintableDocument(html: string, options: PrintJobOptions): string {
  if (isFullDocument(html)) return html;
  const isTicket = (options.kind ?? "ticket") !== "a4";
  const widthMm = options.widthMm ?? 80;
  const pageRule = isTicket
    ? `@page { size: ${widthMm}mm auto; margin: 0; }`
    : `@page { size: A4; margin: 14mm; }`;
  const bodyRule = isTicket
    ? `body { width: ${widthMm}mm; margin: 0; padding: 3mm 0 12mm; font-family: Arial, Helvetica, "Segoe UI", Roboto, sans-serif; font-size: 13px; line-height: 1.5; color: #000; }`
    : `body { margin: 0; font-family: "Segoe UI", Arial, sans-serif; font-size: 12px; color: #1a1a1a; }`;
  return `<!doctype html><html lang="es"><head><meta charset="utf-8" /><title>Impresión</title><style>
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body { margin: 0; padding: 0; background: #fff; -webkit-text-size-adjust: 100%; text-size-adjust: 100%; }
    ${pageRule}
    ${bodyRule}
    table { width: 100%; border-collapse: collapse; }
    td { padding: 1mm 0; }
    .right { text-align: right; }
    .center { text-align: center; }
    .bold { font-weight: 700; }
    hr { border: 0; border-top: 1px dashed #000; margin: 2mm 0; }
  </style></head><body>${html}</body></html>`;
}

/**
 * Loads a complete HTML document into a dedicated OFF-SCREEN iframe and prints
 * ONLY that document. This keeps the invoice as real vector text (fully sharp,
 * never a screenshot of the app) and guarantees the POS interface can never
 * bleed into the receipt or clip its right side.
 */
function printDocumentInHiddenFrame(fullHtml: string, options: PrintJobOptions): void {
  if (typeof document === "undefined") return;
  void options;

  const previous = document.getElementById(PRINT_FRAME_ID);
  if (previous) previous.remove();

  const iframe = document.createElement("iframe");
  iframe.id = PRINT_FRAME_ID;
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("tabindex", "-1");
  iframe.setAttribute("title", "Impresión de factura");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "1px";
  iframe.style.height = "1px";
  iframe.style.border = "0";
  iframe.style.opacity = "0";
  iframe.style.pointerEvents = "none";
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  const doc = win?.document;
  if (!win || !doc) {
    iframe.remove();
    window.print();
    return;
  }

  const cleanup = () => window.setTimeout(() => iframe.remove(), 2000);

  const trigger = () => {
    try {
      win.focus();
      win.print();
    } finally {
      cleanup();
    }
  };

  doc.open();
  doc.write(fullHtml);
  doc.close();

  // Wait for fonts/images so nothing prints half-rendered, then print.
  let attempts = 0;
  const waitForAssets = () => {
    attempts += 1;
    const imagesReady = Array.from(doc.images ?? []).every((img) => img.complete);
    if (imagesReady || attempts > 40) {
      window.setTimeout(trigger, 60);
      return;
    }
    window.setTimeout(waitForAssets, 50);
  };
  window.setTimeout(waitForAssets, 80);
}

/**
 * Prints an HTML fragment. Uses the silent desktop bridge when available
 * (direct thermal/A4 printing) and falls back to printing ONLY the document
 * from an off-screen iframe otherwise (never the visible app window).
 */
export function printHtml(html: string, options: PrintJobOptions = {}): void {
  const bridge = getDesktopBridge();
  if (bridge?.printSilent) {
    printViaSilentBridge(bridge, html, options);
    return;
  }
  if (bridge?.print) {
    const request: RkcDesktopPrintRequest = {
      html,
      kind: options.kind ?? "ticket",
      printer: options.printer ?? null,
      widthMm: options.widthMm,
      copies: options.copies ?? 1,
      openDrawer: options.openDrawer ?? false,
    };
    Promise.resolve(bridge.print(request)).catch(() => {
      printDocumentInHiddenFrame(ensurePrintableDocument(html, options), options);
    });
    return;
  }
  printDocumentInHiddenFrame(ensurePrintableDocument(html, options), options);
}

interface TestPrintOptions extends PrintJobOptions {
  widthMm: number;
  companyName: string;
  rnc?: string | null;
  address?: string | null;
  phone?: string | null;
}

/** Builds and prints a small test ticket to validate the printer setup. */
export function printTestTicket(options: TestPrintOptions): void {
  const { widthMm, companyName, rnc, address, phone } = options;
  const html = `
    <div class="center">
      <strong>${companyName}</strong><br />
      ${rnc ? `RNC: ${rnc}<br />` : ""}
      ${address ? `${address}<br />` : ""}
      ${phone ? `Tel: ${phone}<br />` : ""}
    </div>
    <hr />
    <div class="center"><strong>PRUEBA DE IMPRESIÓN</strong></div>
    <hr />
    <table>
      <tr><td>Ancho</td><td class="right">${widthMm} mm</td></tr>
      <tr><td>Fecha</td><td class="right">${new Date().toLocaleString("es-DO")}</td></tr>
      <tr><td>Estado</td><td class="right">OK</td></tr>
    </table>
    <hr />
    <div class="center">Impresión funcional.<br />Gracias.</div>
  `;
  printHtml(html, {
    kind: "ticket",
    printer: options.printer ?? null,
    widthMm,
    copies: options.copies ?? 1,
    openDrawer: options.openDrawer ?? false,
  });
}

/** Opens the cash drawer through the desktop bridge, if supported. */
export function openCashDrawer(printer?: string | null): void {
  const bridge = getDesktopBridge();
  if (bridge?.openDrawer) {
    Promise.resolve(bridge.openDrawer(printer ?? null)).catch(() => undefined);
  }
}

/**
 * Prints a fully-formed HTML document (used by invoice templates).
 * Delegates to the silent desktop bridge when present, otherwise opens the browser dialog.
 */
export function printFullHtml(fullHtml: string, options: PrintJobOptions = {}): void {
  const bridge = getDesktopBridge();
  if (bridge?.printSilent) {
    printViaSilentBridge(bridge, fullHtml, options);
    return;
  }
  if (bridge?.print) {
    const request: RkcDesktopPrintRequest = {
      html: fullHtml,
      kind: options.kind ?? "a4",
      printer: options.printer ?? null,
      widthMm: options.widthMm,
      copies: options.copies ?? 1,
      openDrawer: options.openDrawer ?? false,
    };
    Promise.resolve(bridge.print(request)).catch(() => {
      printDocumentInHiddenFrame(fullHtml, options);
    });
    return;
  }
  printDocumentInHiddenFrame(fullHtml, options);
}